/**
 * Period Navigator Component
 * 
 * Modern navigation between budget periods.
 */
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ChevronLeft, ChevronRight, Calendar, Loader2, Clock } from 'lucide-react';
import { ProgressIndicator } from './ProgressIndicator';
import type { BudgetPeriod } from '@/types/financial';

interface PeriodNavigatorProps {
  currentPeriod?: BudgetPeriod;
  periods?: BudgetPeriod[];
  isLoading?: boolean;
  onNavigate: (direction: 'previous' | 'next' | 'current') => void;
  onSelectPeriod?: (period: BudgetPeriod) => void;
  className?: string;
  variant?: 'default' | 'boxed';
}

export function PeriodNavigator({
  currentPeriod,
  periods = [],
  isLoading = false,
  onNavigate,
  onSelectPeriod,
  className,
  variant = 'default',
}: Readonly<PeriodNavigatorProps>) {
  const formatPeriodLabel = (period: BudgetPeriod) => {
    const start = parseISO(period.start_date);
    const end = parseISO(period.end_date);
    return `${format(start, 'dd MMM', { locale: ptBR })} - ${format(end, 'dd MMM yyyy', { locale: ptBR })}`;
  };

  const formatShortLabel = (period: BudgetPeriod) => {
    const start = parseISO(period.start_date);
    const end = parseISO(period.end_date);
    return `${format(start, 'dd/MM', { locale: ptBR })} - ${format(end, 'dd/MM', { locale: ptBR })}`;
  };

  const calculateProgress = (period: BudgetPeriod) => {
    const start = parseISO(period.start_date).getTime();
    const end = parseISO(period.end_date).getTime();
    const now = Date.now();
    const total = end - start;
    const elapsed = Math.min(now - start, total);
    return Math.max(0, Math.round((elapsed / total) * 100));
  };

  const getDaysInfo = (period: BudgetPeriod): { days: number; label: string } => {
    const start = parseISO(period.start_date);
    const end = parseISO(period.end_date);
    const now = new Date();
    
    // Set all times to midnight for accurate day comparison
    const startMidnight = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const endMidnight = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    const nowMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // Total duration of the period: (endDate - startDate) + 1 to include both days
    // e.g., 15/12 to 14/01 = 31 days (including both start and end dates)
    const msPerDay = 1000 * 60 * 60 * 24;
    const totalDays = Math.round((endMidnight.getTime() - startMidnight.getTime()) / msPerDay) + 1;
    
    // Check if now is within the period
    const isCurrentPeriod = nowMidnight >= startMidnight && nowMidnight <= endMidnight;
    
    if (isCurrentPeriod) {
      // Show remaining days for current period (including today)
      const remaining = Math.round((endMidnight.getTime() - nowMidnight.getTime()) / msPerDay) + 1;
      return { days: Math.max(0, remaining), label: remaining === 1 ? 'dia' : 'dias' };
    }
    
    // For past or future periods, show total duration
    return { days: totalDays, label: totalDays === 1 ? 'dia' : 'dias' };
  };

  const getPONumber = (period: BudgetPeriod): number => {
    // Regra: o número do PO é o mês de início (independente do dia)
    // Ex.: início 29/01 -> PO 1
    const start = parseISO(period.start_date);
    return start.getMonth() + 1;
  };

  const previousPeriods = currentPeriod
    ? periods.filter((p) => p.id !== currentPeriod.id)
    : periods;
  // Sempre permitir navegação para trás; períodos anteriores são criados sob demanda no backend.
  // Isso viabiliza visualizar POs anteriores mesmo para contas novas.
  const hasPrevious = true;
  // Next is always allowed - backend creates future periods automatically
  const hasNext = true;
  
  // Verifica se a data de HOJE está dentro do período visualizado
  // (não depende do campo is_current, que indica apenas qual período está sendo visualizado)
  const isRealCurrentPeriod = currentPeriod ? (() => {
    const now = new Date();
    const start = parseISO(currentPeriod.start_date);
    const end = parseISO(currentPeriod.end_date);

    // Compara apenas por data (00:00) para manter o dia final do PO inclusivo.
    const nowMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startMidnight = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const endMidnight = new Date(end.getFullYear(), end.getMonth(), end.getDate());

    return nowMidnight >= startMidnight && nowMidnight <= endMidnight;
  })() : false;

  if (isLoading) {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Carregando...</span>
      </div>
    );
  }

  if (!currentPeriod) {
    return (
      <div className={cn('text-sm text-muted-foreground', className)}>
        Nenhum período encontrado
      </div>
    );
  }

  const progress = calculateProgress(currentPeriod);
  const daysInfo = getDaysInfo(currentPeriod);
  const poNumber = getPONumber(currentPeriod);

  const showDaysInButton = variant !== 'boxed';
  const todayFormatted = format(new Date(), 'dd/MM', { locale: ptBR });
  const daysBadgeText = `Dias Restantes: ${daysInfo.days} - Hoje é dia ${todayFormatted}`;

  const renderPeriodPopover = (trigger: React.ReactElement) => (
    <Popover>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-72" align="end">
        <div className="space-y-4">
          <div>
            <h4 className="font-medium text-sm mb-1">Período Orçamentário</h4>
            <p className="text-sm text-muted-foreground">
              {formatPeriodLabel(currentPeriod)}
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Progresso</span>
              <span className="font-medium">{progress}%</span>
            </div>
            <ProgressIndicator
              value={progress}
              showPercentage={false}
              size="sm"
              colorScheme="auto"
            />
          </div>

          <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/50 text-sm">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">
              {daysInfo.days === 0
                ? 'Último dia do período'
                : `${daysInfo.days} ${daysInfo.label}`}
            </span>
          </div>

          {previousPeriods.length > 0 && (
            <div className="border-t pt-3">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                Períodos anteriores
              </p>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {previousPeriods.slice(0, 5).map((period) => (
                  <button
                    key={period.id}
                    onClick={() => onSelectPeriod?.(period)}
                    className={cn(
                      'w-full text-left px-2 py-1.5 rounded text-sm transition-colors',
                      period.id === currentPeriod.id
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    )}
                  >
                    {formatShortLabel(period)}
                    {period.is_current && (
                      <span className="ml-1.5 text-xs bg-primary/20 text-primary px-1.5 py-0.5 rounded">
                        atual
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );

  const defaultPeriodTrigger = renderPeriodPopover(
    <Button
      variant="outline"
      className="h-9 px-3 gap-2 text-sm font-normal border-border/60 hover:bg-muted/50"
    >
      <Calendar className="h-3.5 w-3.5 text-primary" />
      <span className="font-medium">{formatShortLabel(currentPeriod)}</span>
      {showDaysInButton && (
        <span className="text-muted-foreground hidden sm:inline">
          • {daysInfo.days} {daysInfo.label}
        </span>
      )}
    </Button>
  );

  const boxedPeriodTrigger = renderPeriodPopover(
    <Button
      variant="ghost"
      className="h-8 px-2 text-base font-medium text-foreground hover:bg-muted"
    >
      {formatShortLabel(currentPeriod)}
    </Button>
  );

  const navButtons = (
    <div className="flex items-center gap-1">
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-muted-foreground hover:text-foreground"
        disabled={!hasPrevious || isLoading}
        onClick={() => onNavigate('previous')}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      {defaultPeriodTrigger}
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-muted-foreground hover:text-foreground"
        disabled={!hasNext || isLoading}
        onClick={() => onNavigate('next')}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );

  const backToCurrentButton = !isRealCurrentPeriod ? (
    <Button
      variant="outline"
      size="sm"
      className="h-8 px-3 text-sm font-medium bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200"
      disabled={isLoading}
      onClick={() => onNavigate('current')}
    >
      Voltar ao período atual
    </Button>
  ) : null;

  if (variant === 'boxed') {
    return (
      <div
        data-tour-id="dashboard-period-card"
        className={cn(
          'w-full sm:min-w-[360px] sm:w-auto rounded-xl border border-border/60 bg-card/60 px-4 sm:px-6 py-4 sm:py-5 shadow-sm',
          className
        )}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm sm:text-base font-semibold text-foreground">
            Período Orçamentário
          </span>
        </div>
        <div className="mt-2 sm:mt-3 flex items-center gap-1 sm:gap-2 flex-wrap">
          <span className="text-sm sm:text-base font-semibold text-primary">
            PO {poNumber}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 sm:h-8 sm:w-8 text-muted-foreground hover:text-foreground"
            disabled={!hasPrevious || isLoading}
            onClick={() => onNavigate('previous')}
            data-tour-id="dashboard-period-arrow-prev"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          {boxedPeriodTrigger}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 sm:h-8 sm:w-8 text-muted-foreground hover:text-foreground"
            disabled={!hasNext || isLoading}
            onClick={() => onNavigate('next')}
            data-tour-id="dashboard-period-arrow-next"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="mt-2 text-xs sm:text-base text-muted-foreground">
          {daysBadgeText}
        </div>
        {backToCurrentButton && <div className="pt-2 sm:pt-3">{backToCurrentButton}</div>}
      </div>
    );
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {navButtons}
      {backToCurrentButton}
    </div>
  );
}

export default PeriodNavigator;
